import { Table, Tag } from 'antd';
import React, { useCallback, useMemo } from 'react';
import {
  getStatusIcon,
  getTypeColor,
  getTypeLabel,
} from '../../../../../utils/fn/medias';
import MediaTagsInline from './MediaTagsInline';
import MediaThumbnail from './MediaThumbnail';

const MediaTableView = ({
  medias,
  selectedMediaIds,
  onBulkToggle,
  isLoading,
}) => {
  const onSelectChange = useCallback(
    (newSelectedRowKeys) => {
      if (onBulkToggle) {
        onBulkToggle(newSelectedRowKeys);
      }
    },
    [onBulkToggle]
  );

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys: selectedMediaIds || [],
      preserveSelectedRowKeys: true,
      onChange: onSelectChange,
      selections: [
        Table.SELECTION_ALL,
        Table.SELECTION_INVERT,
        Table.SELECTION_NONE,
      ],
      hideSelectAll: true,
    }),
    [selectedMediaIds, onSelectChange]
  );

  const columns = useMemo(
    () => [
      {
        title: 'Aperçu',
        dataIndex: 'thumbnail',
        key: 'thumbnail',
        width: 100,
        render: (thumbnail, record) => (
          <MediaThumbnail
            media={record}
            size="small"
            style={{ borderRadius: '4px' }}
          />
        ),
      },
      {
        title: 'Nom',
        dataIndex: 'name',
        key: 'name',
        ellipsis: true,
        sorter: (a, b) => a.name.localeCompare(b.name),
      },
      {
        title: 'Tags',
        key: 'tags',
        width: 200,
        render: (_, record) => {
          const list = record.tags ?? [];
          if (!list.length) return '-';
          return <MediaTagsInline tags={list} maxVisible={2} size="small" />;
        },
      },
      {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        width: 100,
        render: (type) => (
          <Tag color={getTypeColor(type)}>{getTypeLabel(type)}</Tag>
        ),
        filters: [
          { text: 'Image', value: 'image' },
          { text: 'Vidéo', value: 'video' },
          { text: 'Document', value: 'document' },
        ],
        onFilter: (value, record) => record.type === value,
      },
      {
        title: 'Statut',
        dataIndex: 'statusLabel',
        key: 'status',
        width: 150,
        render: (statusLabel, record) => (
          <Tag icon={getStatusIcon(record.status)} color={record.statusColor}>
            {statusLabel}
          </Tag>
        ),
        filters: [
          { text: 'À qualifier', value: 'to_qualify' },
          { text: 'À valider', value: 'to_validate' },
          { text: 'Validé', value: 'validated' },
          { text: 'Rejeté', value: 'rejected' },
          { text: 'Archivé', value: 'archived' },
        ],
        onFilter: (value, record) => record.status === value,
      },
      {
        title: 'Taille',
        dataIndex: 'size',
        key: 'size',
        width: 120,
        sorter: (a, b) => parseFloat(a.size) - parseFloat(b.size),
      },
      {
        title: 'Date',
        dataIndex: 'date',
        key: 'date',
        width: 120,
        sorter: (a, b) =>
          new Date(a.createdAt || a.date) - new Date(b.createdAt || b.date),
      },
    ],
    []
  );

  return (
    <div style={{ padding: '8px 16px', flex: 1, overflowY: 'auto' }}>
      <Table
        dataSource={medias}
        columns={columns}
        rowKey="id"
        rowSelection={rowSelection}
        loading={isLoading}
        pagination={false}
        scroll={{ y: 'calc(100vh - 390px)' }}
      />
    </div>
  );
};

export default MediaTableView;
